import React, { useState, useMemo } from "react";

// ── Avalanche-matching color tokens ──────────────────────────────────────
const C = {
  bg: "#F0F6FB",
  surface: "#FFFFFF",
  primary: "#2A6591",
  primaryDark: "#1C4E72",
  text1: "#0F2D42",
  text2: "#3A5F78",
  text3: "#7A9BB0",
  border: "rgba(42,101,145,.10)",
  shadow: "0 2px 4px rgba(15,45,66,.04), 0 8px 28px rgba(42,101,145,.08)",
};

const INCIDENT_TYPES = {
  FLOOD: { color: "#3B82F6", icon: "💧", label: "Flood" },
  Flood: { color: "#3B82F6", icon: "💧", label: "Flood" },
  COLD_WAVE: { color: "#6B7280", icon: "❄️", label: "Cold Wave" },
  "Cold Wave": { color: "#6B7280", icon: "❄️", label: "Cold Wave" },
  EARTHQUAKE: { color: "#8B5CF6", icon: "🌍", label: "Earthquake" },
  Earthquake: { color: "#8B5CF6", icon: "🌍", label: "Earthquake" },
  FIRE: { color: "#DC2626", icon: "🔥", label: "Fire" },
  Fire: { color: "#DC2626", icon: "🔥", label: "Fire" },
  CYCLONE: { color: "#F59E0B", icon: "🌀", label: "Cyclone" },
  Cyclone: { color: "#F59E0B", icon: "🌀", label: "Cyclone" },
  "Heat Wave": { color: "#F97316", icon: "🔥", label: "Heat Wave" },
  HEAT_WAVE: { color: "#F97316", icon: "🔥", label: "Heat Wave" },
  Drought: { color: "#D97706", icon: "🌵", label: "Drought" },
  DROUGHT: { color: "#D97706", icon: "🌵", label: "Drought" },
  Landslide: { color: "#92400E", icon: "⛰️", label: "Landslide" },
  LANDSLIDE: { color: "#92400E", icon: "⛰️", label: "Landslide" },
  Tsunami: { color: "#0891B2", icon: "🌊", label: "Tsunami" },
  TSUNAMI: { color: "#0891B2", icon: "🌊", label: "Tsunami" },
  Tornado: { color: "#7C3AED", icon: "🌪️", label: "Tornado" },
  TORNADO: { color: "#7C3AED", icon: "🌪️", label: "Tornado" },
  AVALANCHE: { color: "#9333EA", icon: "🏔️", label: "Avalanche" },
  Avalanche: { color: "#9333EA", icon: "🏔️", label: "Avalanche" },
  CLOUDBURST: { color: "#60A5FA", icon: "⛈️", label: "Cloud Burst" },
  "Cloud Burst": { color: "#60A5FA", icon: "⛈️", label: "Cloud Burst" },
  CloudBurst: { color: "#60A5FA", icon: "⛈️", label: "Cloud Burst" },
  Cloud_Burst: { color: "#60A5FA", icon: "⛈️", label: "Cloud Burst" },
  DEFAULT: { color: "#EF4444", icon: "⚠️", label: "Incident" },
};

const OpenLayerMapLegend = ({
  incidentMarkers,
  expandedIncidentId,
  allIncidents,
  showIncidentMarkers,
  showDistrictBoundaries,
  showStateBoundary,
  mapInstance,
  isPopupOpen: isPopupOpenProp,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeItem, setActiveItem] = useState(null);

  const isPopupOpen = isPopupOpenProp ?? false;
  const effectiveExpanded = isPopupOpen ? false : isExpanded;

  const handleLegendItemClick = (item) => {
    if (!mapInstance || !item.positions || item.positions.length === 0) return;

    setActiveItem(item.key);
    setTimeout(() => setActiveItem(null), 2000);

    if (item.positions.length === 1) {
      const [lat, lng] = item.positions[0];
      mapInstance.panTo({ lat, lng });
      mapInstance.setZoom(15);
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      item.positions.forEach(([lat, lng]) => {
        bounds.extend(new window.google.maps.LatLng(lat, lng));
      });
      mapInstance.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      window.google.maps.event.addListenerOnce(mapInstance, "idle", () => {
        if (mapInstance.getZoom() > 14) mapInstance.setZoom(14);
      });
    }
  };

  const legendItems = useMemo(() => {
    const items = [];
    const markersToShow = expandedIncidentId
      ? incidentMarkers.filter((m) => m.id === expandedIncidentId)
      : incidentMarkers;

    if (showIncidentMarkers && markersToShow.length > 0) {
      const incidentGroups = {};

      markersToShow.forEach((marker) => {
        const category = marker.category || "DEFAULT";
        const config = INCIDENT_TYPES[category] || INCIDENT_TYPES.DEFAULT;

        if (category === "Cloud Burst" || category.includes("Cloud")) {
          console.log("🔍 Cloud Burst Debug:", {
            category, configFound: !!INCIDENT_TYPES[category],
            configLabel: config.label, configIcon: config.icon,
            markerId: marker.id, markerName: marker.name,
          });
        }

        if (!incidentGroups[config.label]) {
          incidentGroups[config.label] = {
            type: "incident", key: `incident-${config.label}`,
            emoji: config.icon, color: config.color,
            label: config.label, borderColor: config.color,
            positions: [],
          };
        }

        if (marker.position && marker.position.length === 2) {
          incidentGroups[config.label].positions.push(marker.position);
        }
      });

      Object.values(incidentGroups).forEach((group) => {
        items.push({ ...group, label: `${group.label} (${group.positions.length})` });
      });
    }

    return items;
  }, [incidentMarkers, expandedIncidentId, showIncidentMarkers, showDistrictBoundaries, showStateBoundary]);

  if (legendItems.length === 0) return null;

  return (
    <div style={{
      backgroundColor: "rgba(255, 255, 255, 0.97)",
      backdropFilter: "blur(12px)",
      borderRadius: 14,
      boxShadow: C.shadow,
      border: `1px solid ${C.border}`,
      width: "auto",
      minWidth: "150px",
      maxHeight: "calc(100% - 40px)",
      overflow: "hidden",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      fontFamily: "'Inter', sans-serif",
      opacity: isPopupOpen ? 0.35 : 1,
      pointerEvents: isPopupOpen ? "none" : "auto",
    }}>
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          gap: 10,
          background: `linear-gradient(135deg, ${C.primaryDark} 0%, ${C.primary} 100%)`,
          color: "white",
          cursor: "pointer",
          borderRadius: effectiveExpanded ? "14px 14px 0 0" : "14px",
          fontSize: 12,
          fontWeight: 700,
          fontFamily: "'Sora',sans-serif",
          userSelect: "none",
          letterSpacing: "-.01em",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          🗺️ Legend
          <span style={{
            backgroundColor: "rgba(255,255,255,0.2)",
            borderRadius: 10, padding: "1px 7px",
            fontSize: 10, fontWeight: 600,
            fontFamily: "'Inter',sans-serif",
          }}>
            {legendItems.length}
          </span>
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg viewBox="0 0 24 24" fill="none" width="13" height="13"
               style={{ opacity: 0.8, marginTop: 1 }}
               title="Click an item below to zoom"
               stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <svg
            viewBox="0 0 12 12" fill="none" width="10" height="10"
            style={{
              transition: "transform 0.25s cubic-bezier(.4,0,.2,1)",
              transform: effectiveExpanded ? "rotate(0deg)" : "rotate(180deg)",
              marginLeft: 2
            }}
          >
            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </div>

      {/* Items */}
      {effectiveExpanded && (
        <div style={{ padding: "8px 6px", overflowY: "auto", maxHeight: "350px" }}>
          {legendItems.map((item) => {
            const hasPositions = item.positions && item.positions.length > 0;
            const isActive = activeItem === item.key;

            return (
              <div
                key={item.key}
                onClick={() => hasPositions && handleLegendItemClick(item)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 8px",
                  borderRadius: 10,
                  marginBottom: 3,
                  cursor: hasPositions ? "pointer" : "default",
                  transition: "all 0.2s cubic-bezier(.16,1,.3,1)",
                  backgroundColor: isActive ? `${item.color}10` : "transparent",
                  border: isActive ? `1px solid ${item.color}30` : "1px solid transparent",
                }}
                onMouseEnter={(e) => {
                  if (hasPositions) {
                    e.currentTarget.style.backgroundColor = isActive ? `${item.color}15` : "rgba(42,101,145,.05)";
                    e.currentTarget.style.transform = "translateX(2px)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isActive ? `${item.color}10` : "transparent";
                  e.currentTarget.style.transform = "translateX(0)";
                }}
                title={hasPositions ? `Click to zoom to ${item.label}` : item.label}
              >
                {/* Icon — Custom Red Border */}
                <div style={{
                  width: 26, height: 26,
                  borderRadius: "50%",
                  background: `${item.color}12`,
                  border: `2px solid #EF4444`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 13, flexShrink: 0,
                  transition: "transform .2s cubic-bezier(.34,1.56,.64,1), box-shadow .2s",
                  boxShadow: isActive ? `0 2px 8px ${item.color}20` : "none",
                }}>
                  {item.emoji}
                </div>

                {/* Label */}
                <span style={{
                  fontSize: 11.5,
                  color: isActive ? item.color : C.text1,
                  fontWeight: isActive ? 700 : 500,
                  lineHeight: 1.3, flex: 1,
                  transition: "color .2s, font-weight .2s",
                }}>
                  {item.label}
                </span>

              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OpenLayerMapLegend;